import { createAction, props } from '@ngrx/store';
import { AppStateFailure } from '@symphony-solo/shared/common-state/src';
import { AccountDetailsFlowType } from '../enum';
import {
  AccountDetails,
  AccountPasswordResetRequest,
  ChangePassword,
  CredentialsModel,
  EmailDomains,
  PersonalDetails,
  TokenResult,
} from '../models';
import { AccountCreate, AccountProperties } from '../models/account.model';

export enum AuthenticationActionTypes {
  Authenticate = '[Authentication] Authenticate',
  AuthenticateSuccess = '[Authentication] AuthenticateSuccess',
  AuthenticateError = '[Authentication] AuthenticateError',
  SoftCheckAuthentication = '[Authentication] SoftCheckAuthentication',
  SoftCheckAuthenticationSuccess = '[Authentication] SoftCheckAuthenticationSuccess',
  SoftCheckAuthenticationError = '[Authentication] SoftCheckAuthenticationError',
  LoadAccountDetails = '[Authentication] LoadAccountDetails',
  LoadAccountDetailsSuccess = '[Authentication] LoadAccountDetailsSuccess',
  LoadAccountDetailsError = '[Authentication] LoadAccountDetailsError',
  SetTokenAndRefreshtokenInfo = '[Authentication] SetTokenAndRefreshtokenInfo',
  LogoutAction = '[Authentication] Logout',
  LogoutSuccessAction = '[Authentication] LogoutSuccess',
  GoHomeAction = '[Authentication] GoHome',
  GoHomeSuccessAction = '[Authentication] GoHomeSuccess',
  SendValidationEmailAction = '[Authentication] SendValidationEmail',
  SendValidationEmailSuccessAction = '[Authentication] SendValidationEmailSuccess',
  SendValidationEmailErrorAction = '[Authentication] SendValidationEmailError',
  SendResetPasswordEmailAction = '[Authentication] SendResetPasswordEmailAction',
  SendResetPasswordEmailSuccessAction = '[Authentication] SendResetPasswordEmailSuccess',
  SendResetPasswordEmailErrorAction = '[Authentication] SendResetPasswordEmailError',
  AccountResetPasswordAction = '[Authentication] AccountResetPasswordAction',
  AccountResetPasswordSuccessAction = '[Authentication] AccountResetPasswordSuccess',
  AccountResetPasswordErrorAction = '[Authentication] AccountResetPasswordError',
  CreateAccountAction = '[Authentication] CreateAccountAction',
  CreateAccountSuccessAction = '[Authentication] CreateAccountSuccess',
  CreateAccountErrorAction = '[Authentication] CreateAccountError',
  PersonalDetailsAction = '[Authentication] PersonalDetailsAction',
  PersonalDetailsSuccessAction = '[Authentication] PersonalDetailsSuccess',
  PersonalDetailsErrorAction = '[Authentication] PersonalDetailsError',
  ChangePasswordAction = '[Authentication] ChangePasswordAction',
  ChangePasswordSuccessAction = '[Authentication] ChangePasswordSuccess',
  ChangePasswordErrorAction = '[Authentication] ChangePasswordError',
  GetAccountPropertiesAction = '[Authentication] GetAccountPropertiesAction',
  GetAccountPropertiesSuccessAction = '[Authentication] GetAccountPropertiesSuccessAction',
  GetAccountPropertiesErrorAction = '[Authentication] GetAccountPropertiesErrorAction',
  GetEmailDomainsAction = '[Authentication] GetEmailDomainsAction',
  GetEmailDomainsSuccessAction = '[Authentication] GetEmailDomainsSuccessAction',
  GetEmailDomainsErrorAction = '[Authentication] GetEmailDomainsErrorAction',
  DeactivateAccountAction = '[Authentication] DeactivateAccountAction',
  DeactivateAccountSuccessAction = '[Authentication] DeactivateAccountSuccessAction',
  DeactivateAccountErrorAction = '[Authentication] DeactivateAccountErrorAction',
  TokenForValidationAction = '[Authentication] TokenForValidationAction',
  TokenForValidationSuccessAction = '[Authentication] TokenForValidationSuccessAction',
  TokenForValidationErrorAction = '[Authentication] TokenForValidationErrorAction',
  SetIsLoadingAction = '[Authentication] SetIsLoadingAction',
  ClearCartAfterFiveHrs = '[Cart] ClearCartAfterFiveHrs',
}

export const authenticateAction = createAction(AuthenticationActionTypes.Authenticate, props<{ credentials: CredentialsModel }>());
export const authenticateSuccessAction = createAction(AuthenticationActionTypes.AuthenticateSuccess, props<{ tokenResult: TokenResult }>());
export const authenticateErrorAction = createAction(AuthenticationActionTypes.AuthenticateError, props<{ failure: AppStateFailure }>());
export const softCheckAuthenticationAction = createAction(AuthenticationActionTypes.SoftCheckAuthentication);
export const softCheckAuthenticationSuccessAction = createAction(AuthenticationActionTypes.SoftCheckAuthenticationSuccess);
export const softCheckAuthenticationErrorAction = createAction(AuthenticationActionTypes.SoftCheckAuthenticationError);
export const loadAccountDetailsAction = createAction(
  AuthenticationActionTypes.LoadAccountDetails,
  props<{ accountDetailsFlowType: AccountDetailsFlowType }>()
);
export const loadAccountDetailsSuccessAction = createAction(
  AuthenticationActionTypes.LoadAccountDetailsSuccess,
  props<{ accountDetails: AccountDetails; accountDetailsFlowType: AccountDetailsFlowType }>()
);
export const loadAccountDetailsErrorAction = createAction(
  AuthenticationActionTypes.LoadAccountDetailsError,
  props<{ failure: AppStateFailure }>()
);

export const setTokenAndRefreshTokenAction = createAction(
  AuthenticationActionTypes.SetTokenAndRefreshtokenInfo,
  props<{ tokenInfo: TokenResult }>()
);
export const logoutAction = createAction(AuthenticationActionTypes.LogoutAction);
export const logoutSuccessAction = createAction(AuthenticationActionTypes.LogoutSuccessAction, props<{ withNavigation?: boolean }>());
export const goHomeAction = createAction(AuthenticationActionTypes.GoHomeAction);
export const goHomeSuccessAction = createAction(AuthenticationActionTypes.GoHomeSuccessAction);
export const sendValidationEmailAction = createAction(AuthenticationActionTypes.SendValidationEmailAction);
export const sendValidationEmailSuccessAction = createAction(AuthenticationActionTypes.SendValidationEmailSuccessAction);
export const sendValidationEmailErrorAction = createAction(
  AuthenticationActionTypes.SendValidationEmailErrorAction,
  props<{ failure: AppStateFailure }>()
);
export const sendResetPasswordEmailAction = createAction(
  AuthenticationActionTypes.SendResetPasswordEmailAction,
  props<{ email: string }>()
);
export const sendResetPasswordEmailSuccessAction = createAction(AuthenticationActionTypes.SendResetPasswordEmailSuccessAction);
export const sendResetPasswordEmailErrorAction = createAction(
  AuthenticationActionTypes.SendResetPasswordEmailErrorAction,
  props<{ failure: AppStateFailure }>()
);
export const AccountResetPasswordAction = createAction(
  AuthenticationActionTypes.AccountResetPasswordAction,
  props<{ payload: AccountPasswordResetRequest }>()
);
export const AccountResetPasswordSuccessAction = createAction(AuthenticationActionTypes.AccountResetPasswordSuccessAction);
export const AccountResetPasswordErrorAction = createAction(
  AuthenticationActionTypes.AccountResetPasswordErrorAction,
  props<{ failure: AppStateFailure }>()
);

export const createAccountAction = createAction(AuthenticationActionTypes.CreateAccountAction, props<{ createAccount: AccountCreate }>());
export const createAccountSuccessAction = createAction(AuthenticationActionTypes.CreateAccountSuccessAction);
export const createAccountErrorAction = createAction(
  AuthenticationActionTypes.CreateAccountErrorAction,
  props<{ failure: AppStateFailure }>()
);

export const personalDetailsAction = createAction(
  AuthenticationActionTypes.PersonalDetailsAction,
  props<{ personalDetails: PersonalDetails }>()
);
export const personalDetailsSuccessAction = createAction(AuthenticationActionTypes.PersonalDetailsSuccessAction);
export const personalDetailsErrorAction = createAction(
  AuthenticationActionTypes.PersonalDetailsErrorAction,
  props<{ failure: AppStateFailure }>()
);

export const changePasswordAction = createAction(
  AuthenticationActionTypes.ChangePasswordAction,
  props<{ changePassword: ChangePassword }>()
);
export const changePasswordSuccessAction = createAction(
  AuthenticationActionTypes.ChangePasswordSuccessAction,
  props<{ responseCode: string }>()
);
export const changePasswordErrorAction = createAction(
  AuthenticationActionTypes.ChangePasswordErrorAction,
  props<{ failure: AppStateFailure }>()
);

export const getAccountPropertiesAction = createAction(AuthenticationActionTypes.GetAccountPropertiesAction);
export const getAccountPropertiesSuccessAction = createAction(
  AuthenticationActionTypes.GetAccountPropertiesSuccessAction,
  props<{ accountProperties: AccountProperties }>()
);
export const getAccountPropertiesErrorAction = createAction(
  AuthenticationActionTypes.GetAccountPropertiesErrorAction,
  props<{ failure: AppStateFailure }>()
);

export const getEmailDomainsAction = createAction(AuthenticationActionTypes.GetEmailDomainsAction);
export const getEmailDomainsSuccessAction = createAction(
  AuthenticationActionTypes.GetEmailDomainsSuccessAction,
  props<{ emailDomains: EmailDomains }>()
);
export const getEmailDomainsErrorAction = createAction(
  AuthenticationActionTypes.GetEmailDomainsErrorAction,
  props<{ failure: AppStateFailure }>()
);

export const deactivateAccountAction = createAction(AuthenticationActionTypes.DeactivateAccountAction);
export const deactivateAccountSuccessAction = createAction(AuthenticationActionTypes.DeactivateAccountSuccessAction);
export const deactivateAccountErrorAction = createAction(
  AuthenticationActionTypes.DeactivateAccountErrorAction,
  props<{ failure: AppStateFailure }>()
);

export const tokenForValidationAction = createAction(AuthenticationActionTypes.TokenForValidationAction);
export const tokenForValidationSuccessAction = createAction(AuthenticationActionTypes.TokenForValidationSuccessAction);
export const tokenForValidationErrorAction = createAction(AuthenticationActionTypes.TokenForValidationErrorAction);
export const setIsLoadingAction = createAction(AuthenticationActionTypes.SetIsLoadingAction, props<{ isLoading: boolean }>());
export const clearCartAfterFiveHrsAction = createAction(AuthenticationActionTypes.ClearCartAfterFiveHrs);
